import type { ComponentProps, ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * Die eine Fläche, auf der in Kontor alles steht.
 *
 * Vorher baute jede Seite ihren Rahmen selbst — 26 Stellen mit „border rounded-lg border", jede
 * mit eigenen Abständen. Deshalb driftete die Optik von Seite zu Seite: nicht aus
 * Nachlässigkeit, sondern weil es nichts zum Wiederverwenden gab. Jetzt entscheidet diese Datei,
 * wie eine Fläche aussieht, und eine Änderung hier gilt überall.
 */
export function Panel({ className, ...props }: ComponentProps<'section'>) {
  return (
    <section
      className={cn(
        'bg-surface border-border shadow-xs overflow-hidden rounded-xl border',
        className,
      )}
      {...props}
    />
  )
}

export interface PanelHeaderProps extends Omit<ComponentProps<'header'>, 'title'> {
  title: ReactNode
  /** Eine Zeile darunter, wenn der Titel allein die Fläche nicht erklärt. */
  description?: ReactNode
  /** Zählwerte, Badges, Knöpfe — landet rechts und bricht bei Platzmangel um. */
  actions?: ReactNode
}

export function PanelHeader({
  title,
  description,
  actions,
  className,
  ...props
}: PanelHeaderProps) {
  return (
    <header
      className={cn(
        'border-border flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-3',
        className,
      )}
      {...props}
    >
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>
        )}
      </div>
      {actions === undefined ? null : (
        <div className="ml-auto flex flex-wrap items-center gap-1.5">{actions}</div>
      )}
    </header>
  )
}

/** Für Inhalt, der kein Zeilenwerk ist: Text, Formulare, Erklärungen. */
export function PanelBody({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('space-y-3 px-4 py-3.5 text-sm', className)} {...props} />
}

/**
 * Eine Zeile in einer Liste von Flächen.
 *
 * Der letzte Trennstrich entfällt, weil der Rand der Fläche ihn schon zieht — zwei Linien
 * übereinander sind der sichtbarste Hinweis darauf, dass niemand hingesehen hat.
 */
export function PanelRow({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'border-border/70 flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-3 text-sm last:border-b-0',
        className,
      )}
      {...props}
    />
  )
}

/** Wenn eine Fläche nichts zu zeigen hat, ohne dafür eine ganze leere Seite zu rechtfertigen. */
export function PanelEmpty({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('text-muted-foreground px-4 py-6 text-sm', className)} {...props} />
}
